import type {
  ApiResponse,
  Vehiculo,
  Ruta,
  Jornada,
  AbrirJornadaPayload,
  CargarStockPayload,
  ResumenCargaStock,
  CierreJornadaPayload,
  ResumenCierre,
  SaldoCliente,
  VentaPendiente,
  EmpresaPerfil,
  UpdateEmpresaPayload,
  EmpleadoItem,
  VehiculoAdminItem,
  RutaAdminItem,
  UnidadMedidaItem,
  ProductoAdminItem,
  ProductoAdminDetalle,
  ClienteAdminItem,
  ClienteAdminDetalle,
  SucursalAdminItem,
  CreateClientePayload,
  UpdateClientePayload,
  CreateSucursalPayload,
  UpdateSucursalPayload,
  ProveedorAdminItem,
  CreateProveedorPayload,
  UpdateProveedorPayload,
  AsociarProveedorResponse,
  OrdenCompraItem,
  OrdenCompraDetalle,
  CreateOrdenCompraPayload,
  UpdateOrdenCompraPayload,
  PatchVencimientoPagoPayload,
  CreatePagoProveedorPayload,
  RegistrarPagoResponse,
  RegistrarRecepcionPayload,
  RecepcionResponseData,
} from '@/types';

// ─── Configuration ────────────────────────────────────────────────────────────

const API_BASE_URL = import.meta.env.VITE_SIGLO_API_URL ?? 'http://localhost:8787';

// Timeout por defecto para peticiones al servidor (ms)
const DEFAULT_TIMEOUT_MS = 10_000;

// ─── Custom Errors ────────────────────────────────────────────────────────────

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | undefined,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

export class NetworkError extends Error {
  constructor(message = 'Sin conexión a internet') {
    super(message);
    this.name = 'NetworkError';
  }
}

export class TimeoutError extends Error {
  constructor(message = 'La petición tardó demasiado') {
    super(message);
    this.name = 'TimeoutError';
  }
}

// ─── Core Fetch Wrapper ───────────────────────────────────────────────────────

/**
 * Opciones extendidas para peticiones al API de SIGLO.
 */
interface SigloRequestInit extends Omit<RequestInit, 'body'> {
  /** Timeout en ms. Default: 10 000ms */
  timeoutMs?: number;
  /** Body como objeto — será serializado automáticamente a JSON */
  json?: unknown;
  /** Body raw (string / FormData). Alternativa a json. */
  body?: BodyInit | null;
}

/**
 * Ejecuta una petición al API de SIGLO.
 * - Agrega Content-Type y Accept automáticamente.
 * - Incluye credentials: 'include' para que las cookies HttpOnly viajen.
 * - Maneja timeout via AbortController.
 * - Lanza errores tipados para fácil captura en la UI.
 */
async function request<T>(
  path: string,
  options: SigloRequestInit = {},
): Promise<T> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, json, ...fetchOptions } = options;

  // AbortController para timeout
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort('timeout'), timeoutMs);

  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;

  const headers = new Headers(fetchOptions.headers);
  headers.set('Accept', 'application/json');
  if (json !== undefined) {
    headers.set('Content-Type', 'application/json');
  }

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      credentials: 'include',       // Cookies HttpOnly automáticas
      signal: controller.signal,
      headers,
      body: json !== undefined ? JSON.stringify(json) : fetchOptions.body,
    });

    clearTimeout(timer);

    // Respuestas 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    // Parsear JSON
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      let code: string | undefined;
      let message = `HTTP ${response.status}`;
      let details: unknown;

      if (data && typeof data === 'object') {
        if ('error' in data && data.error) {
          if (typeof data.error === 'object') {
            const errObj = data.error as { code?: string; message?: string; details?: unknown };
            code = errObj.code;
            message = errObj.message || message;
            details = errObj.details;
          } else if (typeof data.error === 'string') {
            message = data.error;
            code = (data as { code?: string }).code;
            details = (data as { details?: unknown }).details;
          }
        } else if ('message' in data && typeof data.message === 'string') {
          message = data.message;
          code = (data as { code?: string }).code;
          details = (data as { details?: unknown }).details;
        }
      }

      throw new ApiRequestError(
        response.status,
        code,
        message,
        details,
      );
    }

    return data as T;
  } catch (error) {
    clearTimeout(timer);

    if (error instanceof ApiRequestError) {
      if (error.status === 401 && unauthorizedCallback) {
        unauthorizedCallback(path);
      }
      throw error;
    }

    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new TimeoutError();
    }

    // TypeError de fetch = sin red
    if (error instanceof TypeError || !navigator.onLine) {
      throw new NetworkError();
    }

    throw error;
  }
}

let unauthorizedCallback: ((path: string) => void) | null = null;

export function registerUnauthorizedCallback(callback: (path: string) => void) {
  unauthorizedCallback = callback;
}

// ─── Public API Client ────────────────────────────────────────────────────────

/**
 * Cliente tipado para el API de SIGLO.
 * Todos los métodos lanzan NetworkError cuando no hay conexión —
 * el llamador puede capturarlo y encolar la operación offline.
 */
export const api = {
  /**
   * GET /path
   */
  get<T>(path: string, options?: SigloRequestInit): Promise<T> {
    return request<T>(path, { ...options, method: 'GET' });
  },

  /**
   * POST /path con body JSON
   */
  post<T>(path: string, body: unknown, options?: SigloRequestInit): Promise<T> {
    return request<T>(path, { ...options, method: 'POST', json: body });
  },

  /**
   * PUT /path con body JSON
   */
  put<T>(path: string, body: unknown, options?: SigloRequestInit): Promise<T> {
    return request<T>(path, { ...options, method: 'PUT', json: body });
  },

  /**
   * PATCH /path con body JSON parcial
   */
  patch<T>(path: string, body: unknown, options?: SigloRequestInit): Promise<T> {
    return request<T>(path, { ...options, method: 'PATCH', json: body });
  },

  /**
   * DELETE /path
   */
  delete<T>(path: string, options?: SigloRequestInit): Promise<T> {
    return request<T>(path, { ...options, method: 'DELETE' });
  },

  /**
   * Comprueba si el servidor es alcanzable haciendo GET /api/v1/salud.
   * Útil antes de intentar sincronización.
   */
  async isReachable(): Promise<boolean> {
    try {
      await request('/api/v1/salud', { method: 'GET', timeoutMs: 4_000 });
      return true;
    } catch {
      return false;
    }
  },
} as const;

// ─── ADR-017 Lote 1: Panel y Configuración Pública ───────────────────────────

export interface PanelResumenData {
  pedidosHoy: number;
  montoHoy: number;
}

/**
 * Obtiene las métricas del panel de control (pedidos y monto acumulado hoy).
 * Endpoint ADR-017 Lote 1: GET /api/v1/panel
 */
export async function getPanel(): Promise<ApiResponse<PanelResumenData>> {
  return api.get<ApiResponse<PanelResumenData>>('/api/v1/panel');
}

/**
 * Obtiene la configuración pública de la empresa (contacto, WhatsApp, etc.).
 * Endpoint ADR-017 Lote 1: GET /api/v1/configuracion/publica
 */
export async function getConfiguracionPublica<T = Record<string, unknown>>(): Promise<ApiResponse<T>> {
  return api.get<ApiResponse<T>>('/api/v1/configuracion/publica');
}

// ─── ADR-017 Lote 2: Catálogo Público ──────────────────────────────────────────

export interface CatalogoFilters {
  marca?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Obtiene el catálogo de productos públicos con filtros y paginación opcionales.
 * Endpoint ADR-017 Lote 2: GET /api/v1/catalogo
 */
export async function getCatalogoPublico<T = unknown>(
  params?: CatalogoFilters
): Promise<ApiResponse<T>> {
  const searchParams = new URLSearchParams();
  if (params?.marca) searchParams.set('marca', params.marca);
  if (params?.q) searchParams.set('q', params.q);
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));

  const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
  return api.get<ApiResponse<T>>(`/api/v1/catalogo${query}`);
}

/**
 * Obtiene el listado de marcas disponibles en el catálogo público.
 * Endpoint ADR-017 Lote 2: GET /api/v1/catalogo/marcas
 */
export async function getCatalogoMarcas<T = string[]>(): Promise<T> {
  return api.get<T>('/api/v1/catalogo/marcas');
}

/**
 * Obtiene el detalle de un producto del catálogo público por ID.
 * Endpoint ADR-017 Lote 2: GET /api/v1/catalogo/:id
 */
export async function getCatalogoProductoById<T = unknown>(
  id: number | string
): Promise<ApiResponse<T>> {
  return api.get<ApiResponse<T>>(`/api/v1/catalogo/${id}`);
}

/**
 * Obtiene el catálogo completo del servidor (compatibilidad).
 * Retorna la respuesta paginada de productos.
 */
export async function fetchCatalog<T>(params?: CatalogoFilters): Promise<ApiResponse<T>> {
  return getCatalogoPublico<T>(params);
}

/**
 * Obtiene los clientes asignados al vendedor autenticado.
 */
export async function fetchClientes<T>(): Promise<ApiResponse<T>> {
  return api.get<ApiResponse<T>>('/api/v1/clientes');
}

// ─── Jornada helpers ──────────────────────────────────────────────────────────

/**
 * Obtiene los vehículos disponibles del abonado.
 */
export async function fetchVehiculos(): Promise<ApiResponse<Vehiculo[]>> {
  return api.get<ApiResponse<Vehiculo[]>>('/api/v1/vehiculos');
}

/**
 * Obtiene las rutas activas del abonado.
 */
export async function fetchRutas(): Promise<ApiResponse<Ruta[]>> {
  return api.get<ApiResponse<Ruta[]>>('/api/v1/rutas');
}

/**
 * Obtiene la jornada abierta activa del vendedor autenticado (o null si no hay).
 */
export async function fetchJornadaActiva(): Promise<ApiResponse<Jornada | null>> {
  return api.get<ApiResponse<Jornada | null>>('/api/v1/jornadas/activa');
}

/**
 * Obtiene el detalle completo de una jornada por ID.
 */
export async function fetchJornadaDetalle(id: number): Promise<ApiResponse<Jornada>> {
  return api.get<ApiResponse<Jornada>>(`/api/v1/jornadas/${id}`);
}

/**
 * Abre una nueva jornada para el vendedor autenticado.
 * Requiere conexión activa (no debe encolarse offline).
 */
export async function abrirJornada(payload: AbrirJornadaPayload): Promise<ApiResponse<Jornada>> {
  return api.post<ApiResponse<Jornada>>('/api/v1/jornadas', payload);
}

/**
 * Carga stock desde depósito hacia el vehículo asignado a la jornada.
 * Requiere conexión activa (no debe encolarse offline).
 */
export async function cargarStockVehiculo(
  idJornada: string,
  payload: CargarStockPayload,
): Promise<ApiResponse<ResumenCargaStock>> {
  return api.post<ApiResponse<ResumenCargaStock>>(`/api/v1/jornadas/${idJornada}/carga`, payload);
}

/**
 * Cierra la jornada activa, concilia stock y devuelve resumen de ventas/cobros.
 * Requiere conexión activa (no debe encolarse offline).
 */
export async function cerrarJornada(
  idJornada: string,
  payload?: CierreJornadaPayload,
): Promise<ApiResponse<ResumenCierre>> {
  return api.post<ApiResponse<ResumenCierre>>(`/api/v1/jornadas/${idJornada}/cierre`, payload ?? {});
}

// ─── ADR-013 / ADR-017 Lote 5: Cuentas Corrientes, Ventas y Cobros ───────────

/**
 * Obtiene el resumen de saldos pendientes de todos los clientes.
 * Endpoint ADR-017 Lote 5: GET /api/v1/ventas/clientes/saldos
 */
export async function getClientesSaldosPendientes(): Promise<ApiResponse<SaldoCliente[]>> {
  return api.get<ApiResponse<SaldoCliente[]>>('/api/v1/ventas/clientes/saldos');
}

/**
 * Obtiene el listado de ventas con cobro pendiente para un cliente específico.
 * Endpoint ADR-017 Lote 5: GET /api/v1/ventas/cliente/:id/pendientes
 */
export async function getVentasPendientesByCliente(
  clienteId: number
): Promise<ApiResponse<VentaPendiente[]>> {
  return api.get<ApiResponse<VentaPendiente[]>>(
    `/api/v1/ventas/cliente/${clienteId}/pendientes`
  );
}

/**
 * Obtiene el listado de cobros registrados para una venta.
 * Endpoint ADR-017 Lote 5: GET /api/v1/ventas/:ventaId/cobros
 */
export async function getCobrosDeVenta<T = unknown>(
  ventaId: number | string
): Promise<ApiResponse<T>> {
  return api.get<ApiResponse<T>>(`/api/v1/ventas/${ventaId}/cobros`);
}

// ─── ADR-014: Empresa / Administración ────────────────────────────────────────

/**
 * Obtiene el perfil de la empresa del abonado.
 */
export async function getEmpresaPerfil(): Promise<ApiResponse<EmpresaPerfil>> {
  return api.get<ApiResponse<EmpresaPerfil>>('/api/v1/admin/empresa');
}

/**
 * Actualiza los datos de la empresa del abonado.
 */
export async function updateEmpresaPerfil(
  payload: UpdateEmpresaPayload
): Promise<ApiResponse<EmpresaPerfil>> {
  return api.patch<ApiResponse<EmpresaPerfil>>('/api/v1/admin/empresa', payload);
}

// ─── ADR-014 Entrega B: Empleados ─────────────────────────────────────────────

/**
 * Obtiene el listado de empleados del abonado con filtro opcional por activo.
 */
export async function getEmpleados(activo?: 0 | 1): Promise<ApiResponse<EmpleadoItem[]>> {
  const query = activo !== undefined ? `?activo=${activo}` : '';
  return api.get<ApiResponse<EmpleadoItem[]>>(`/api/v1/admin/empleados${query}`);
}

/**
 * Obtiene el detalle de un empleado por ID.
 */
export async function getEmpleadoById(id: number): Promise<ApiResponse<EmpleadoItem>> {
  return api.get<ApiResponse<EmpleadoItem>>(`/api/v1/admin/empleados/${id}`);
}

/**
 * Crea un nuevo empleado.
 */
export async function createEmpleado(payload: unknown): Promise<ApiResponse<EmpleadoItem>> {
  return api.post<ApiResponse<EmpleadoItem>>('/api/v1/admin/empleados', payload);
}

/**
 * Actualiza un empleado existente.
 */
export async function updateEmpleado(
  id: number,
  payload: unknown
): Promise<ApiResponse<EmpleadoItem>> {
  return api.patch<ApiResponse<EmpleadoItem>>(`/api/v1/admin/empleados/${id}`, payload);
}

// ─── ADR-014 Entrega B: Vehículos ─────────────────────────────────────────────

/**
 * Obtiene el listado de todos los vehículos en modo gestión.
 */
export async function getVehiculosAdmin(): Promise<ApiResponse<VehiculoAdminItem[]>> {
  return api.get<ApiResponse<VehiculoAdminItem[]>>('/api/v1/admin/vehiculos');
}

/**
 * Crea un nuevo vehículo.
 */
export async function createVehiculo(payload: unknown): Promise<ApiResponse<VehiculoAdminItem>> {
  return api.post<ApiResponse<VehiculoAdminItem>>('/api/v1/admin/vehiculos', payload);
}

/**
 * Actualiza un vehículo existente.
 */
export async function updateVehiculo(
  id: number,
  payload: unknown
): Promise<ApiResponse<VehiculoAdminItem>> {
  return api.patch<ApiResponse<VehiculoAdminItem>>(`/api/v1/admin/vehiculos/${id}`, payload);
}

// ─── ADR-014 Entrega B: Rutas ─────────────────────────────────────────────────

/**
 * Obtiene el listado de todas las rutas en modo gestión.
 */
export async function getRutasAdmin(): Promise<ApiResponse<RutaAdminItem[]>> {
  return api.get<ApiResponse<RutaAdminItem[]>>('/api/v1/admin/rutas');
}

/**
 * Crea una nueva ruta.
 */
export async function createRuta(payload: unknown): Promise<ApiResponse<RutaAdminItem>> {
  return api.post<ApiResponse<RutaAdminItem>>('/api/v1/admin/rutas', payload);
}

/**
 * Actualiza una ruta existente.
 */
export async function updateRuta(
  id: number,
  payload: unknown
): Promise<ApiResponse<RutaAdminItem>> {
  return api.patch<ApiResponse<RutaAdminItem>>(`/api/v1/admin/rutas/${id}`, payload);
}

// ─── ADR-014 Entrega B Prompt B: Productos ───────────────────────────────────

/**
 * Obtiene las unidades de medida disponibles para productos.
 */
export async function getUnidadesMedida(): Promise<ApiResponse<UnidadMedidaItem[]>> {
  return api.get<ApiResponse<UnidadMedidaItem[]>>('/api/v1/admin/productos/unidades');
}

/**
 * Obtiene el listado de productos en modo gestión con filtro opcional por activo y por id_proveedor.
 */
export async function getProductosAdmin(
  filtersOrActivo?: (0 | 1) | { activo?: 0 | 1; id_proveedor?: number },
  idProveedor?: number
): Promise<ApiResponse<ProductoAdminItem[]>> {
  const params = new URLSearchParams();
  if (typeof filtersOrActivo === 'object' && filtersOrActivo !== null) {
    if (filtersOrActivo.activo !== undefined) params.set('activo', String(filtersOrActivo.activo));
    if (filtersOrActivo.id_proveedor !== undefined) params.set('id_proveedor', String(filtersOrActivo.id_proveedor));
  } else {
    if (filtersOrActivo !== undefined) params.set('activo', String(filtersOrActivo));
    if (idProveedor !== undefined) params.set('id_proveedor', String(idProveedor));
  }
  const query = params.toString() ? `?${params.toString()}` : '';
  return api.get<ApiResponse<ProductoAdminItem[]>>(`/api/v1/admin/productos${query}`);
}

/**
 * Obtiene el detalle de un producto para edición.
 */
export async function getProductoAdminById(
  id: number
): Promise<ApiResponse<ProductoAdminDetalle>> {
  return api.get<ApiResponse<ProductoAdminDetalle>>(`/api/v1/admin/productos/${id}`);
}

/**
 * Crea un nuevo producto en modo gestión.
 */
export async function createProducto(
  payload: unknown
): Promise<ApiResponse<ProductoAdminDetalle>> {
  return api.post<ApiResponse<ProductoAdminDetalle>>('/api/v1/admin/productos', payload);
}

/**
 * Actualiza un producto existente en modo gestión.
 */
export async function updateProducto(
  id: number,
  payload: unknown
): Promise<ApiResponse<ProductoAdminDetalle>> {
  return api.patch<ApiResponse<ProductoAdminDetalle>>(`/api/v1/admin/productos/${id}`, payload);
}

// ─── ADR-014 / ADR-017 Lote 3: Clientes y Sucursales Admin ───────────────────

/**
 * Obtiene el listado de clientes en modo gestión con filtros opcionales (q, activo, paginación).
 * Endpoint ADR-017 Lote 3: GET /api/v1/clientes
 */
export async function getClientesAdmin(
  filters?: { q?: string; activo?: boolean; page?: number; pageSize?: number }
): Promise<ApiResponse<ClienteAdminItem[]>> {
  const params = new URLSearchParams();
  if (filters?.page) params.set('page', String(filters.page));
  if (filters?.pageSize) params.set('pageSize', String(filters.pageSize));
  if (filters?.q) params.set('q', filters.q);
  if (filters?.activo !== undefined) params.set('activo', String(filters.activo));

  const query = params.toString() ? `?${params.toString()}` : '';
  return api.get<ApiResponse<ClienteAdminItem[]>>(`/api/v1/clientes${query}`);
}

/**
 * Obtiene el detalle completo de un cliente y sus sucursales por ID.
 * Endpoint ADR-017 Lote 3: GET /api/v1/clientes/:id
 */
export async function getClienteById(
  id: number
): Promise<ApiResponse<ClienteAdminDetalle>> {
  return api.get<ApiResponse<ClienteAdminDetalle>>(`/api/v1/clientes/${id}`);
}

/**
 * Crea un nuevo cliente con su sucursal principal inicial.
 * Endpoint ADR-017 Lote 3: POST /api/v1/clientes
 */
export async function createCliente(
  payload: CreateClientePayload
): Promise<ApiResponse<ClienteAdminDetalle>> {
  return api.post<ApiResponse<ClienteAdminDetalle>>('/api/v1/clientes', payload);
}

/**
 * Actualiza los datos de un cliente existente.
 * Endpoint ADR-017 Lote 3: PATCH /api/v1/clientes/:id
 */
export async function updateCliente(
  id: number,
  payload: UpdateClientePayload
): Promise<ApiResponse<ClienteAdminDetalle>> {
  return api.patch<ApiResponse<ClienteAdminDetalle>>(`/api/v1/clientes/${id}`, payload);
}

/**
 * Crea una sucursal adicional para un cliente.
 * Endpoint ADR-017 Lote 3: POST /api/v1/clientes/:id/sucursales
 */
export async function createSucursal(
  clienteId: number,
  payload: CreateSucursalPayload
): Promise<ApiResponse<SucursalAdminItem>> {
  return api.post<ApiResponse<SucursalAdminItem>>(
    `/api/v1/clientes/${clienteId}/sucursales`,
    payload
  );
}

/**
 * Actualiza los datos de una sucursal de un cliente.
 * Endpoint ADR-017 Lote 3: PATCH /api/v1/clientes/:id/sucursales/:sucursalId
 */
export async function updateSucursal(
  clienteId: number,
  sucursalId: number,
  payload: UpdateSucursalPayload
): Promise<ApiResponse<SucursalAdminItem>> {
  return api.patch<ApiResponse<SucursalAdminItem>>(
    `/api/v1/clientes/${clienteId}/sucursales/${sucursalId}`,
    payload
  );
}

// ─── ADR-017: Proveedores Admin ───────────────────────────────────────────────

/**
 * Obtiene el listado de proveedores en modo gestión con filtros opcionales (q, activo, paginación).
 */
export async function getProveedoresAdmin(
  filters?: { q?: string; activo?: boolean | 'all'; page?: number; pageSize?: number }
): Promise<ApiResponse<ProveedorAdminItem[]>> {
  const params = new URLSearchParams();
  if (filters?.page) params.set('page', String(filters.page));
  if (filters?.pageSize) params.set('pageSize', String(filters.pageSize));
  if (filters?.q) params.set('q', filters.q);
  if (filters?.activo !== undefined) params.set('activo', String(filters.activo));

  const query = params.toString() ? `?${params.toString()}` : '';
  return api.get<ApiResponse<ProveedorAdminItem[]>>(`/api/v1/proveedores${query}`);
}

/**
 * Obtiene el detalle de un proveedor por ID.
 */
export async function getProveedorById(
  id: number
): Promise<ApiResponse<ProveedorAdminItem>> {
  return api.get<ApiResponse<ProveedorAdminItem>>(`/api/v1/proveedores/${id}`);
}

/**
 * Crea un nuevo proveedor comprobando unicidad de RUT.
 */
export async function createProveedor(
  payload: CreateProveedorPayload
): Promise<ApiResponse<ProveedorAdminItem>> {
  return api.post<ApiResponse<ProveedorAdminItem>>('/api/v1/proveedores', payload);
}

/**
 * Actualiza los datos de un proveedor existente (incluye cambios de estado).
 */
export async function updateProveedor(
  id: number,
  payload: UpdateProveedorPayload
): Promise<ApiResponse<ProveedorAdminItem>> {
  return api.patch<ApiResponse<ProveedorAdminItem>>(`/api/v1/proveedores/${id}`, payload);
}

/**
 * Baja lógica (soft delete) de un proveedor en SIGLO.
 */
export async function deleteProveedor(
  id: number
): Promise<ApiResponse<ProveedorAdminItem>> {
  return api.delete<ApiResponse<ProveedorAdminItem>>(`/api/v1/proveedores/${id}`);
}

// ─── ADR-019: Relación Producto - Proveedor ───────────────────────────────────

/**
 * Asocia un proveedor existente a un producto en SIGLO.
 * Body estricto: { id_proveedor: number }
 */
export async function asociarProveedorAProducto(
  idProducto: number,
  idProveedor: number
): Promise<ApiResponse<AsociarProveedorResponse>> {
  return api.post<ApiResponse<AsociarProveedorResponse>>(
    `/api/v1/admin/productos/${idProducto}/proveedores`,
    { id_proveedor: idProveedor }
  );
}

/**
 * Desasocia un proveedor de un producto en SIGLO.
 */
export async function desasociarProveedorDeProducto(
  idProducto: number,
  idProveedor: number
): Promise<ApiResponse<{ success: boolean }>> {
  return api.delete<ApiResponse<{ success: boolean }>>(
    `/api/v1/admin/productos/${idProducto}/proveedores/${idProveedor}`
  );
}

// ─── Stock Depósito: Consulta y Ajustes ───────────────────────────────────────

/** Ítem de stock devuelto por GET /api/v1/stock. */
export interface StockDepositoItem {
  id: number;
  producto: { id: number; nombre: string; codigoBarras?: string | null };
  numeroLote: string;
  fechaVencimiento: string;
  cantidadActual: number;
  unidadBase: string;
  diasParaVencer: number;
  alertaVencimiento: boolean;
}

/** Resultado de POST /api/v1/stock-deposito/ajustes. */
export interface AjusteStockResultado {
  id_producto: number;
  numero_lote: string;
  fecha_vencimiento: string;
  cantidad_anterior: number;
  cantidad_nueva: number;
  lote_creado: boolean;
}

/**
 * Obtiene el stock actual del depósito (todos los lotes con cantidad > 0).
 */
export async function getStockDeposito(): Promise<ApiResponse<StockDepositoItem[]>> {
  return api.get<ApiResponse<StockDepositoItem[]>>('/api/v1/stock');
}

/**
 * Ajusta la cantidad de un lote específico en el depósito.
 * Fija la cantidad (no acumula); si el lote no existe, lo crea.
 */
export async function ajustarStockDeposito(payload: {
  id_producto: number;
  numero_lote: string;
  fecha_vencimiento: string;
  cantidad_nueva: number;
  motivo?: string;
}): Promise<ApiResponse<AjusteStockResultado>> {
  return api.post<ApiResponse<AjusteStockResultado>>(
    '/api/v1/stock-deposito/ajustes',
    payload
  );
}

// ─── ADR-020: Órdenes de Compra (Modo Gestión) ───────────────────────────────

/**
 * Obtiene el listado de órdenes de compra con filtros opcionales (estado, id_proveedor).
 */
export async function getOrdenesCompra(
  filters?: { estado?: string; id_proveedor?: number }
): Promise<ApiResponse<OrdenCompraItem[]>> {
  const params = new URLSearchParams();
  if (filters?.estado) params.set('estado', filters.estado);
  if (filters?.id_proveedor) params.set('id_proveedor', String(filters.id_proveedor));
  const query = params.toString() ? `?${params.toString()}` : '';
  return api.get<ApiResponse<OrdenCompraItem[]>>(`/api/v1/ordenes-compra${query}`);
}

/**
 * Obtiene el detalle completo de una orden de compra por ID (incluye líneas y pagos).
 */
export async function getOrdenCompraById(
  id: number
): Promise<ApiResponse<OrdenCompraDetalle>> {
  return api.get<ApiResponse<OrdenCompraDetalle>>(`/api/v1/ordenes-compra/${id}`);
}

/**
 * Crea una orden de compra nueva en estado 'borrador'.
 */
export async function createOrdenCompra(
  payload: CreateOrdenCompraPayload
): Promise<ApiResponse<OrdenCompraDetalle>> {
  return api.post<ApiResponse<OrdenCompraDetalle>>('/api/v1/ordenes-compra', payload);
}

/**
 * Actualiza una orden de compra en estado 'borrador'.
 */
export async function updateOrdenCompra(
  id: number,
  payload: UpdateOrdenCompraPayload
): Promise<ApiResponse<OrdenCompraDetalle>> {
  return api.patch<ApiResponse<OrdenCompraDetalle>>(`/api/v1/ordenes-compra/${id}`, payload);
}

/**
 * Actualiza la fecha de vencimiento de pago de una orden de compra a crédito.
 * Endpoint: PATCH /api/v1/ordenes-compra/:id/vencimiento-pago (roles: admin)
 * Body: { fecha_vencimiento_pago: string | null }
 */
export async function patchVencimientoPagoOrdenCompra(
  id: number,
  fechaVencimientoPago: string | null
): Promise<ApiResponse<OrdenCompraDetalle>> {
  const payload: PatchVencimientoPagoPayload = {
    fecha_vencimiento_pago: fechaVencimientoPago,
  };
  return api.patch<ApiResponse<OrdenCompraDetalle>>(
    `/api/v1/ordenes-compra/${id}/vencimiento-pago`,
    payload
  );
}

/**
 * Confirma una orden de compra ('borrador' -> 'confirmada').
 */
export async function confirmarOrdenCompra(
  id: number
): Promise<ApiResponse<OrdenCompraDetalle>> {
  return api.patch<ApiResponse<OrdenCompraDetalle>>(`/api/v1/ordenes-compra/${id}/confirmar`, {});
}

/**
 * Cancela una orden de compra ('borrador' o 'confirmada' -> 'cancelada').
 */
export async function cancelarOrdenCompra(
  id: number
): Promise<ApiResponse<OrdenCompraDetalle>> {
  return api.patch<ApiResponse<OrdenCompraDetalle>>(`/api/v1/ordenes-compra/${id}/cancelar`, {});
}

/**
 * Registra un pago sobre una orden de compra confirmada o recibida.
 */
export async function registrarPagoOrdenCompra(
  id: number,
  payload: CreatePagoProveedorPayload
): Promise<ApiResponse<RegistrarPagoResponse>> {
  return api.post<ApiResponse<RegistrarPagoResponse>>(`/api/v1/ordenes-compra/${id}/pagos`, payload);
}

// ─── ADR-020: Recepción de Mercadería ─────────────────────────────────────────

/**
 * Registra la recepción de mercadería contra una orden de compra confirmada.
 * Endpoint: POST /api/v1/recepciones (roles: admin, deposito)
 * Nota de contrato: usa camelCase en el body.
 */
export async function registrarRecepcion(
  payload: RegistrarRecepcionPayload
): Promise<ApiResponse<RecepcionResponseData>> {
  return api.post<ApiResponse<RecepcionResponseData>>('/api/v1/recepciones', payload);
}

